/**
 * ENG-8116: Claude Code occupancy, measured as a DURATION.
 *
 * opencode can bracket real work (`beginTurn`/`endTurn`), so its spans are
 * exact. Claude Code has no turn lifecycle the manager can see — the only
 * signal is `pane.log`'s mtime, which advances while the TUI repaints and stops
 * when the agent is idle. So occupancy has to be reconstructed by watching that
 * mtime advance.
 *
 * ## Why this cannot ride the poll cycle
 *
 * The obvious place to sample is `pollCycle`, which already stats `pane.log`
 * for the responsiveness probe. But the poll cycle is exactly what made the old
 * design wrong: measured on the prod fleet its spacing is 29s at the median and
 * ~180s at p90, because the cycle does network I/O and per-agent tmux/docker
 * work before re-arming. Sampling occupancy at 180s resolution cannot produce
 * per-minute durations.
 *
 * A `statSync` is microseconds, so this samples on its own light timer instead.
 * That is deliberately NOT the same as moving the whole probe onto a timer — the
 * probe performs destructive read-and-reset counter drains and iterates
 * `persistentSessionAgents`, so racing it against `pollCycle` risks losing
 * counters and skipping agents. This does neither: it only stats files and
 * writes to an in-memory ledger. Its worst failure is missing one 10-second
 * sample for one agent, which the next tick corrects.
 *
 * ## Attribution rule
 *
 * Each tick compares `pane.log`'s mtime to the previous observation:
 *
 *   - mtime unchanged  -> no output, nothing accrued
 *   - advanced by <= IDLE_GAP_MS -> the agent was working THROUGHOUT the gap;
 *     accrue the whole span. This is what makes the measurement cadence-
 *     independent: if the timer is delayed, the next sample sees a
 *     proportionally larger gap and accrues proportionally more, so the total
 *     is the same.
 *   - advanced by > IDLE_GAP_MS -> the agent was idle for most of that gap and
 *     then produced a burst. Accrue only one sampling interval, not the gap.
 *
 * The threshold is what makes a quiet stretch INSIDE a turn still count: a
 * Claude Code agent running a 45-second `pnpm test` writes nothing to the pane
 * while it waits, but it is occupied — it is blocked on a tool it invoked. That
 * agent was being skipped entirely under the old freshness check (it reported
 * `pane_age ~= 60` and failed the window), which is a second, cadence-
 * independent source of under-count that this rule fixes as a side effect.
 *
 * The bias is deliberately conservative at transitions. Crediting the full gap
 * on a long idle would over-charge, and over-charging a customer is a worse
 * failure than the under-count this issue exists to remove.
 */

import { statSync } from 'node:fs';

import type { BusyBucketLedger } from './busy-bucket-ledger.js';
import { sharedBusyBuckets } from './busy-bucket-ledger.js';
import { paneLogPath } from './persistent-session.js';

/** How often the sampler ticks. Cheap enough to be well under a minute. */
export const SAMPLE_INTERVAL_MS = 10_000;

/**
 * Longest silent stretch still counted as occupied. Covers a quiet tool call
 * (a test run, a long bash step) without crediting a genuinely idle agent.
 * Matches the 60s the old freshness window implied, so this does not widen what
 * "busy" means — it only stops the measurement depending on probe cadence.
 */
export const IDLE_GAP_MS = 120_000;

/**
 * Occupancy credited to a burst that follows a gap longer than IDLE_GAP_MS.
 *
 * We know output happened AT mtime and that nothing preceded it for the whole
 * gap, so the honest span is unknown — this is a policy choice, not a
 * measurement. One minute treats any output as at least a minute of engagement,
 * which is the deliberate round-up-toward-the-customer-charge direction.
 *
 * Note what this controls under the whole-minute billing policy: not how many
 * seconds are credited, but how many MINUTES the burst touches. A 60s span
 * straddling a minute boundary marks two minutes, so a single post-idle burst
 * can bill up to two minutes. Bounded and intentional; crediting the full gap
 * instead would bill thirty minutes for one message after thirty minutes of
 * silence, which is not defensible as a measurement.
 */
export const POST_IDLE_CREDIT_MS = 60_000;

/** Last observed pane.log mtime per agent. */
const lastMtimeMs = new Map<string, number>();

/**
 * ENG-9061: wall-clock time of the last SUCCESSFUL pane.log stat per agent.
 *
 * Deliberately separate from {@link lastMtimeMs}, which records what the file
 * said rather than when we last managed to read it. The two diverge in exactly
 * the case that matters: an idle agent's mtime never advances, so `lastMtimeMs`
 * stops changing while the sampler is perfectly healthy — and conversely, an
 * agent whose pane.log becomes unreadable keeps its last mtime forever, because
 * the eviction sweep below only drops agents that left the live set. Presence in
 * `lastMtimeMs` is therefore not evidence that the meter is running; a timestamp
 * is.
 *
 * This is what {@link hasFreshPaneObservation} answers, and it is the only thing
 * entitled to stand the legacy billing cron down (see the occupancy roster in
 * manager-worker's responsiveness probe). Standing a fallback producer down on
 * an assertion nobody checked would convert a detectable over-bill into a silent
 * under-bill, which is strictly worse: an over-bill has a complainant.
 */
const lastObservedAtMs = new Map<string, number>();

/**
 * How stale a pane observation may be and still count as "the meter is live".
 *
 * Six sampler ticks. Loose enough to ride out an event-loop stall or a slow
 * `statSync` on a busy host; far tighter than the cron's 10-minute stand-down
 * window, and that gap is the property that makes this safe. A sampler that dies
 * drops every agent out of the roster within a minute, so the cron resumes
 * billing well before any occupancy could go unbilled.
 */
export const OBSERVATION_FRESH_MS = SAMPLE_INTERVAL_MS * 6;

/**
 * ENG-9061: has a pane.log read succeeded for this agent recently enough to
 * treat its (possibly empty) occupancy ledger as a measurement, rather than as
 * an absence of measurement?
 *
 * False for every opencode agent, which has no pane.log at all — those accrue
 * occupancy from their turn lifecycle and nothing here observes them, so they
 * are deliberately excluded from the roster and keep the legacy cron path.
 */
export function hasFreshPaneObservation(
  codeName: string,
  nowMs: number,
  maxAgeMs: number = OBSERVATION_FRESH_MS,
): boolean {
  const at = lastObservedAtMs.get(codeName);
  if (at == null) return false;
  return nowMs - at <= maxAgeMs;
}

function paneMtimeMs(codeName: string): number | null {
  try {
    return statSync(paneLogPath(codeName)).mtimeMs;
  } catch {
    // No pane.log: an opencode agent (which reports occupancy from its turn
    // lifecycle instead), or a session that has not started writing yet.
    return null;
  }
}

/**
 * One sampling pass. Exported for tests and driven by {@link startPaneOccupancySampler}.
 *
 * `readMtime` is injectable so the attribution rule can be tested without a
 * filesystem — the rule is the part that decides what a customer is billed.
 */
export function samplePaneOccupancyOnce(
  codeNames: Iterable<string>,
  opts: {
    ledger?: BusyBucketLedger;
    readMtime?: (codeName: string) => number | null;
    /** ENG-9061: injectable clock for the observation watermark. */
    nowMs?: number;
  } = {},
): void {
  const ledger = opts.ledger ?? sharedBusyBuckets;
  const readMtime = opts.readMtime ?? paneMtimeMs;
  const nowMs = opts.nowMs ?? Date.now();

  const seen = new Set<string>();
  for (const codeName of codeNames) {
    seen.add(codeName);
    const mtime = readMtime(codeName);
    if (mtime == null) continue;

    // ENG-9061: stamped on a successful READ, before any of the accrual
    // decisions below. An idle agent takes every `continue` in this loop and
    // still counts as observed — "the meter is running" and "the agent is
    // working" are different facts, and conflating them is what would let an
    // idle agent be mistaken for a dead sampler.
    lastObservedAtMs.set(codeName, nowMs);

    const prev = lastMtimeMs.get(codeName);
    lastMtimeMs.set(codeName, mtime);

    // First observation establishes the baseline only. Accruing against an
    // unknown previous mtime would credit however long the file happened to
    // have existed — on manager restart, potentially days.
    if (prev == null) continue;
    if (mtime <= prev) continue;

    const gap = mtime - prev;
    if (gap <= IDLE_GAP_MS) {
      ledger.accrueSpan(codeName, prev, mtime);
    } else {
      // Idle, then a burst. We know output happened AT mtime and nothing before
      // it for `gap` ms, so the true span is unknowable — credit a fixed
      // allowance rather than the gap. See POST_IDLE_CREDIT_MS.
      ledger.accrueSpan(codeName, mtime - POST_IDLE_CREDIT_MS, mtime);
    }
  }

  // Forget agents that are no longer live, so a codename reused by a new
  // session does not accrue against the dead one's mtime.
  for (const codeName of [...lastMtimeMs.keys()]) {
    if (!seen.has(codeName)) lastMtimeMs.delete(codeName);
  }
  // ENG-9061: same eviction for the observation watermark. A torn-down agent
  // must not keep attesting a live meter from beyond the grave — it would stand
  // the cron down for a codename nothing is sampling.
  for (const codeName of [...lastObservedAtMs.keys()]) {
    if (!seen.has(codeName)) lastObservedAtMs.delete(codeName);
  }
}

let timer: NodeJS.Timeout | null = null;

/**
 * Start the sampler. `liveAgents` is called each tick rather than captured, so
 * the sampler always sees the current session set without holding a reference
 * to mutable manager state.
 */
export function startPaneOccupancySampler(liveAgents: () => Iterable<string>): void {
  if (timer) return;
  timer = setInterval(() => {
    try {
      samplePaneOccupancyOnce(liveAgents());
    } catch {
      // Never let a sampling error kill the manager's event loop. A missed tick
      // costs at most one interval of occupancy for one agent.
    }
  }, SAMPLE_INTERVAL_MS);
  // Do not hold the process open for telemetry.
  timer.unref?.();
}

export function stopPaneOccupancySampler(): void {
  if (!timer) return;
  clearInterval(timer);
  timer = null;
}

/** Test seam. */
export function __resetPaneOccupancyStateForTests(): void {
  lastMtimeMs.clear();
  lastObservedAtMs.clear();
  stopPaneOccupancySampler();
}
